/**
 * Brand Switcher Utility
 * Shared logic for brand switching and preview functionality
 * Used by both Header brand switcher and BrandSwitcherPartial
 */

export const BrandSwitcherUtil = (() => {
    const DEFAULT_SUBDOMAIN = "";
    const DEFAULT_CONFIG = {
        imageBasePath: "/_content/Stater.EConsumer.Portal.UI/image",
        heroFileName: "hero.jpg",
        logoFileName: "logo.svg"
    };

    /**
     * Initialize brand switcher context with DOM elements
     * @returns {Object} Context object with brand switching methods
     */
    function createBrandSwitcherContext() {
        const defaultTokenStylesheet = document.querySelector('link[href*="tokens."][href*=".css"]');
        const heroImage = document.querySelector(".hero img");
        const logoImages = Array.from(document.querySelectorAll(".header-logo img"));

        const originalState = {
            stylesheetHref: defaultTokenStylesheet?.getAttribute("href") || "",
            heroSrc: heroImage?.getAttribute("src") || "",
            logoSrcs: logoImages.map((img) => img.getAttribute("src") || "")
        };

        let brandedTokenLink = null;

        /**
         * Build the branded token stylesheet URL
         */
        function buildBrandedTokenHref(subdomain) {
            if (!originalState.stylesheetHref || !subdomain) {
                return null;
            }

            const url = new URL(originalState.stylesheetHref, window.location.origin);
            url.pathname = url.pathname.replace(
                /tokens\.[^/.]+\.css$/,
                `tokens.${subdomain}.css`
            );

            return url.pathname + url.search;
        }

        /**
         * Set preview theme for a specific brand
         */
        function setPreviewTheme(subdomain) {
            if (subdomain === DEFAULT_SUBDOMAIN) {
                restorePreviewTheme();
                return;
            }

            const brandedHref = buildBrandedTokenHref(subdomain);
            if (!brandedHref) return;

            // Create branded link if it doesn't exist
            if (!brandedTokenLink) {
                brandedTokenLink = document.createElement("link");
                brandedTokenLink.rel = "stylesheet";
                brandedTokenLink.type = "text/css";
                document.head.appendChild(brandedTokenLink);
            }

            // Update href to new brand
            brandedTokenLink.setAttribute("href", brandedHref);

            // Update images
            if (heroImage) {
                heroImage.setAttribute(
                    "src",
                    `${DEFAULT_CONFIG.imageBasePath}/${subdomain}/${DEFAULT_CONFIG.heroFileName}`
                );
            }

            logoImages.forEach((img) => {
                img.setAttribute(
                    "src",
                    `${DEFAULT_CONFIG.imageBasePath}/${subdomain}/${DEFAULT_CONFIG.logoFileName}`
                );
            });
        }

        /**
         * Restore preview to default state
         */
        function restorePreviewTheme() {
            // Remove branded token stylesheet completely from DOM when restoring to default
            if (brandedTokenLink && brandedTokenLink.parentNode) {
                brandedTokenLink.remove();
                brandedTokenLink = null;
            }

            if (heroImage && originalState.heroSrc) {
                heroImage.setAttribute("src", originalState.heroSrc);
            }

            logoImages.forEach((img, index) => {
                const originalSrc = originalState.logoSrcs[index];
                if (originalSrc) {
                    img.setAttribute("src", originalSrc);
                }
            });
        }

        /**
         * Navigate to a different brand's subdomain
         */
        function navigateToSubdomain(subdomain) {
            if (!subdomain) return;

            const url = new URL(window.location.href);
            const parts = url.hostname.split(".");

            if (parts.length < 2) return;

            parts[0] = subdomain;
            url.hostname = parts.join(".");

            window.location.href = url.toString();
        }

        /**
         * Extract subdomain from a navigation link
         */
        function getSubdomainFromLink(link) {
            const explicit = link.dataset.theme;
            if (explicit) return explicit;

            const href = link.getAttribute("href");
            if (!href) return null;

            try {
                const url = new URL(href, window.location.origin);
                const parts = url.hostname.split(".");
                return parts.length > 1 ? parts[0] : null;
            } catch {
                return null;
            }
        }

        // Return public API
        return {
            setPreviewTheme,
            restorePreviewTheme,
            navigateToSubdomain,
            getSubdomainFromLink,
            DEFAULT_SUBDOMAIN
        };
    }

    return {
        createBrandSwitcherContext
    };
})();

// Export to window for use in inline scripts (views/partials)
if (typeof window !== "undefined") {
    window.BrandSwitcherUtil = BrandSwitcherUtil;
}

